import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useAuth } from "shared/contexts/AuthProvider";

export const EVENT_CHANNEL_MESSAGE_RECEIVED = "channel-message";

export const robotConnectionsManagerReducer = (state, action) => {
  switch (action.type) {
    case "ADD_PEER_CONNECTION": {
      return state;
    }

    case "DEL_PEER_CONNECTION": {
      return state;
    }

    default:
      return state;
  }
};

export const initialRobotConnectionsManagerState = {
  connections: new Map(),
  cameraChannels: {},
  controlChannel: null,
  remoteVideoTracks: new Map(),
  remoteAudioTracks: new Map(),
  controlChannels: null,
};

const RobotConnectionsManagerContext = createContext();

export function RobotConnectionsManagerProvider({ children }) {
  const peerConnectionsRef = useRef(new Map());
  const remoteStreamsRef = useRef(new Map()); // Store media streams per peer
  const [connectedPeers, setConnectedPeers] = useState([]); // UI state

  const createPeerConnection = (peerId) => {
    if (peerConnectionsRef.current.has(peerId)) return; // Already exists

    const peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    // Handle incoming tracks
    peerConnection.ontrack = (event) => {
      remoteStreamsRef.current.set(peerId, event.streams[0]);
      setConnectedPeers([...peerConnectionsRef.current.keys()]); // Update UI
    };

    peerConnectionsRef.current.set(peerId, peerConnection);
  };

  const [robotId, setRobotId] = useState(false);
  const [robotStatus, setRobotStatus] = useState(false);
  const [isCommandChannelReady, setIsCommandChannelReady] = useState(false);

  const [robotConnectionsManagerState, dispatch] = useReducer(
    robotConnectionsManagerReducer,
    initialRobotConnectionsManagerState
  );

  const refEventListenners = useRef({});
  const eventListenners = refEventListenners.current;

  /**
   * Add event listener to be notified. This is generally for plugins
   * @param {*} listener
   */
  const addWebRTCEventListener = (eventName, listener) => {
    let listenners;
    if (!eventListenners.hasOwnProperty(eventName)) {
      listenners = [];
      eventListenners[eventName] = listenners;
    } else {
      listenners = eventListenners[eventName];
    }
    listenners.push(listener);
    console.log(
      "addWebRTCEventListener event: ",
      eventName,
      " count: ",
      listenners.length
    );
    return () => {
      removeWebRTCEventListener(eventName, listener);
    };
  };

  const removeWebRTCEventListener = (eventName, listener) => {
    if (eventListenners.hasOwnProperty(eventName)) {
      const listenners = eventListenners[eventName];
      let index = listenners.findIndex((element) => element == listener);
      if (index != -1) {
        listenners.splice(index, 1);
      }
      if (eventListenners[eventName].length == 0) {
        delete eventListenners[eventName];
      }
      console.log(
        "removeWebRTCEventListener event: ",
        eventName,
        " count: ",
        listenners.length
      );
    }
  };

  const countEventListeners = (eventName) => {
    if (!eventListenners.hasOwnProperty(eventName)) return 0;

    return eventListenners[eventName].length;
  };

  const notifyEventListeners = (eventName, eventObj) => {
    if (eventName in eventListenners) {
      const listenters = eventListenners[eventName];
      listenters.forEach((listener) => {
        listener(eventObj);
      });
    }
  };

  const { auth } = useAuth();

  useEffect(() => {}, [auth]);

  const addPeerConnection = ({ peerId, connection }) => {
    dispatch({ type: "ADD_PEER_CONNECTION", payload: { peerId, connection } });
  };

  const setControlChannel = (channelId) => {
    dispatch({ type: "SET_CONTROL_CHANNEL", payload: channelId });
  };

  const sendControlMessage = (message) => {
    if (robotConnectionsManagerState.controlChannels) {
      robotConnectionsManagerState.controlChannels.send(message);
    } else {
      console.error("Control channel is not available");
    }
  };

  const addChannelMessageReceivedEventListener = (listener) => {
    return addWebRTCEventListener(EVENT_CHANNEL_MESSAGE_RECEIVED, listener);
  };

  const addRemoteVideoTrack = (peerId, track) => {
    dispatch({ type: "ADD_REMOTE_VIDEO_TRACK", payload: { peerId, track } });
  };
  const removeRemoteVideoTrack = (peerId, track) => {
    dispatch({ type: "REMOVE_REMOTE_VIDEO_TRACK", payload: { peerId, track } });
  };
  const addRemoteAudioTrack = (peerId, track) => {
    dispatch({ type: "ADD_REMOTE_AUDIO_TRACK", payload: { peerId, track } });
  };
  const removeRemoteAudioTrack = (peerId, track) => {
    dispatch({ type: "REMOVE_REMOTE_AUDIO_TRACK", payload: { peerId, track } });
  };

  const connect = async (robot_id) => {
    // Implement connection logic here
    console.log("Connecting to robot with ID:", robotId);
  };

  const close = () => {
    // Implement disconnection logic here
    console.log("Closing connection to robot with ID:", robotId);
  };

  return (
    <RobotConnectionsManagerContext.Provider
      value={{
        setRobotId,
        robotStatus,
        remoteVideoTracks: robotConnectionsManagerState.remoteVideoTracks,
        remoteAudioTracks: robotConnectionsManagerState.remoteAudioTracks,
        addPeerConnection,
        addRemoteVideoTrack,
        removeRemoteVideoTrack,
        addRemoteAudioTrack,
        removeRemoteAudioTrack,
        dispatch,
        setControlChannel,
        sendControlMessage,
        addChannelMessageReceivedEventListener,
        isCommandChannelReady,
        connect,
        close,
      }}
    >
      {children}
    </RobotConnectionsManagerContext.Provider>
  );
}

export const useRobotConnectionsManager = () =>
  useContext(RobotConnectionsManagerContext);
